import config from "react-global-configuration"
import { all, call, put, select, takeLatest } from "redux-saga/effects"

import { getMedicalPath, getNextDoctor } from "client/optic/saga"
import { getCall } from "containers/Call/services/actions"
import stripeActions from "containers/Stripe/services/actions"
import { languages } from "lib/languages"
import { request } from "lib/request"
import { DoctorType } from "types/entity"
import { ERROR_API } from "types/error"
import {
  Call,
  OpticConsultationRequest,
  ResponseCall,
} from "types/payload"
import { Action, GFlow, GWatcher, Message } from "types/redux"

import actions from "./actions"
import { PRICE_REQUEST, TLC_REQUEST } from "./constants"

async function createConsultationApi(
  payload: OpticConsultationRequest
): Promise<ResponseCall> {
  const opticConsultationUrl = config.get("optic.post.consultation")
  return await request(opticConsultationUrl, {
    method: "POST",
    payload,
  })
}


async function getPricesApi(payload: { relativeId?: string }, is_exempted: boolean) {
  const ophtalmologist = await request(
    `${config.get('call.get.price')}` +
    `?&call_type_value=${DoctorType.OPHTALMOLOGIST}` +
    `${is_exempted ? `&exemption=true` : ``}` +
    `${payload.relativeId ? `&relative_id=${payload.relativeId}` : ``}`,
    {
      method: "GET",
    }
  )
  return Promise.resolve({
    // orthoptist,
    ophtalmologist,
  })
}

function* createConsultation(
  payload: OpticConsultationRequest
): GFlow<ResponseCall> {
  // if exemption is not already set by the customer
  if (!payload.payment.exemption) {
    payload.payment.exemption = yield select((state: any) => state.payment?.isExempted)
  }
  const medicalPathResponse = yield call(getMedicalPath)
  const nextDoctorCategory =
    getNextDoctor(medicalPathResponse) || DoctorType.ORTHOPTIST
  return yield call(createConsultationApi, {
    ...payload,
    call_type: nextDoctorCategory,
  } as OpticConsultationRequest)
}

function* getIsExempted(payload: { relativeId?: string | null, callTypeValue?: DoctorType | null }): any {
  try {
    const response = yield request(config.get("optic.post.is_ophta_repeat_call"), {
      method: "POST",
      payload: {
        relative_id: payload.relativeId,
        call_type_value: DoctorType.OPHTALMOLOGIST
      }
    })
    return !!response.is_ophta_repeat_call;

  } catch (error) {
    return false
  }
}

function* priceFlow({ payload, }: Action<{ relativeId?: string, callTypeValue?: DoctorType }>): any {
  try {
    let is_exempted = false
    is_exempted = yield call(getIsExempted, payload)

    yield put(actions.priceExempt(is_exempted));
    const response = yield call(getPricesApi, payload, is_exempted)
    yield put(actions.priceSuccess(response));
  } catch (error) {
    yield put(
      actions.priceError(
        typeof error === "string" ? error : languages.genericAPIError
      )
    )
  }
}

function* createConsultationFlow({
  payload,
}: Action<OpticConsultationRequest>): GFlow<ResponseCall | Message> {
  yield put(stripeActions.hasStripeError(false))
  try {
    const response: ResponseCall = yield createConsultation(payload)
    const { requires_action, payment_intent_client_secret } = response
    if (requires_action && payment_intent_client_secret)
      yield put(actions.paymentRequiresAction(payment_intent_client_secret))
    else if (response.status === "ok" && response.call) {
      yield all([put(actions.success(response.call as Call)), put(getCall())])
    } else {
      yield put(actions.error("Impossible de charger la tlc"))
    }
  } catch (error: any) {
    if (error?.code && error.code === ERROR_API.STRIPE)
      yield put(stripeActions.hasStripeError(true))

    if (error?.message) yield put(actions.error(error.message))
    else
      yield put(
        actions.error((error as string) ?? "Impossible de charger la tlc")
      )
  }
}

function* paymentWatcher(): GWatcher {
  yield takeLatest(TLC_REQUEST, createConsultationFlow)
  yield takeLatest(PRICE_REQUEST, priceFlow)
}

export default paymentWatcher
